import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDarkMode } from "../../hooks/useDarkMode";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaArrowLeft,
  FaMagic,
  FaSearch,
  FaImage,
  FaPalette,
  FaCheckCircle,
  FaSpinner,
  FaPenSquare,
  FaUpload,
  FaChevronCircleRight,
  FaChevronCircleLeft,
  FaRobot,
  FaRegEdit,
  FaLayerGroup,
  FaPaperPlane,
  FaPlus,
  FaTrash,
  FaLightbulb,
  FaExternalLinkAlt,
  FaCopy,
} from "react-icons/fa";
import {
  generateBlogApi,
  searchImagesApi,
  createBlogActionApi,
  getBlogStatusApi,
} from "../../apis/blog.api";
import { checkSeoEligibilityApi } from "../../apis/payments.api";
import { toast } from "react-toastify";

// small helper component for rendering and navigating the step bar
const StepIndicator = ({ step, setStep }) => {
  const items = [
    { id: 1, label: "Setup", icon: <FaPenSquare /> },
    {
      id: 2,
      label: "Writing",
      icon: <FaSpinner className={step === 2 ? "animate-spin" : ""} />,
    },
    { id: 3, label: "Refine", icon: <FaLayerGroup /> },
    { id: 4, label: "Publish", icon: <FaPaperPlane /> },
  ];

  return (
    <div className="flex items-center bg-gray-200/50 dark:bg-gray-800/50 p-1.5 rounded-2xl backdrop-blur-sm overflow-x-auto">
      {items.map((s, idx) => (
        <React.Fragment key={s.id}>
          <button
            type="button"
            onClick={() => {
              // only allow navigating backwards (completed steps)
              if (s.id < step) setStep(s.id);
            }}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-2xl text-sm font-black transition-all duration-300 ${
              step === s.id
                ? "bg-green-600 text-white shadow-xl scale-105"
                : step > s.id
                  ? "bg-green-100/80 text-green-700 dark:bg-green-900/40 dark:text-green-400"
                  : "text-gray-400 opacity-60 cursor-default"
            }`}
          >
            <span className="text-xl">
              {step > s.id ? <FaCheckCircle /> : s.icon}
            </span>
            <span className="hidden md:block uppercase tracking-wide">
              {s.label}
            </span>
          </button>
          {idx < items.length - 1 && (
            <div className="mx-1 h-0.5 w-4 bg-gray-300 dark:bg-gray-700 rounded-full" />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

const BlogGeneratorPage = () => {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useDarkMode();
  const { projectName, domainUrl } = location.state || {};

  // Step state
  const [step, setStep] = useState(1); // 1: Setup, 2: Writing, 3: Edit/Images, 4: Publish

  // Setup state
  const [setupData, setSetupData] = useState({
    topic: "",
    audience: "Beginners",
    goal: "Educate",
    tone: "Professional",
    length: "medium",
    keywords: "",
    notes: "",
  });

  // Blog content state
  const [blogData, setBlogData] = useState(null);
  const [editableContent, setEditableContent] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);

  // Image state
  const [imageKeyword, setImageKeyword] = useState("");
  const [images, setImages] = useState([]);
  const [isSearchingImages, setIsSearchingImages] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Preview state
  const [activeTab, setActiveTab] = useState("html"); // 'html' or 'preview'
  const editorRef = useRef(null);
  const previewRef = useRef(null);
  const cursorPositionRef = useRef(0);

  // Publish state
  const [publishData, setPublishData] = useState({
    categories: "",
    tags: "",
    colorMode: "default", // 'default' or 'custom'
    customColor: "#008300",
    status: "draft",
  });
  const [isPublishing, setIsPublishing] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [actionStatus, setActionStatus] = useState(null);
  const previewEditableRef = useRef(null);
  const [selectedPreviewImg, setSelectedPreviewImg] = useState(null); // { el, top, left }
  const [showWpModal, setShowWpModal] = useState(false); // WordPress check modal

  const [postUrl, setPostUrl] = useState(null); // WordPress post URL after publishing
  const [postId, setPostId] = useState(null); // WordPress post ID after publishing

  // Subscription / plan states
  const [hasSubscription, setHasSubscription] = useState(false);
  const [trialEligible, setTrialEligible] = useState(false);
  const [trialUsed, setTrialUsed] = useState(false);
  const [isPlanChecking, setIsPlanChecking] = useState(true);

  // Color options
  const colorOptions = ["#008300", "#00cc00", "#4f46e5", "#ec4899", "#f59e0b"];

  // Audience, Goal, Tone presets
  const audiences = [
    "Beginners",
    "Intermediate",
    "Advanced",
    "Students",
    "Professionals",
    "Entrepreneurs",
    "Developers",
    "Marketers",
    "Business Owners",
    "General Public",
    "Executives",
    "Content Creators",
  ];
  const goals = [
    "Educate",
    "Inform",
    "Persuade",
    "Entertain",
    "Inspire",
    "Compare",
    "Review",
    "Tutorial",
    "Thought Leadership",
  ];
  const tones = [
    "Professional",
    "Casual",
    "Friendly",
    "Formal",
    "Humorous",
    "Authoritative",
    "Conversational",
    "Inspiring",
    "Technical",
    "Empathetic",
    "Bold",
    "Minimalist",
  ];

  // Check subscription / trial eligibility on mount
  useEffect(() => {
    const checkEligibility = async () => {
      setIsPlanChecking(true);
      try {
        const res = await checkSeoEligibilityApi({ project_id: projectId });
        const data = res.data;
        setHasSubscription(data.has_subscription || false);
        setTrialEligible(data.trial_eligible || false);
        setTrialUsed(data.trial_used || false);
        // Cache per session
        sessionStorage.setItem(
          "trial_status_user",
          JSON.stringify({
            has_subscription: data.has_subscription || false,
            trial_eligible: data.trial_eligible || false,
            trial_used: data.trial_used || false,
            timestamp: Date.now(),
          }),
        );
      } catch (err) {
        console.error("Plan eligibility check failed:", err);
        // Fallback to session cache
        const cached = sessionStorage.getItem("trial_status_user");
        if (cached) {
          try {
            const d = JSON.parse(cached);
            setHasSubscription(d.has_subscription || false);
            setTrialEligible(d.trial_eligible || false);
            setTrialUsed(d.trial_used || false);
          } catch (_) {}
        }
      } finally {
        setIsPlanChecking(false);
      }
    };
    checkEligibility();
  }, [projectId]);

  // Poll for action status
  useEffect(() => {
    if (!actionId || actionStatus === "completed" || actionStatus === "failed")
      return;

    const interval = setInterval(async () => {
      try {
        const res = await getBlogStatusApi(actionId);
        setActionStatus(res.data.status);
        if (res.data.status === "completed") {
          if (res.data.wordpress_post_url)
            setPostUrl(res.data.wordpress_post_url);
          if (res.data.wordpress_post_id) setPostId(res.data.wordpress_post_id);
          toast.success("Blog published to WordPress!");
          clearInterval(interval);
        } else if (res.data.status === "failed") {
          toast.error(res.data.error_message || "Publishing failed");
          clearInterval(interval);
        }
      } catch (err) {
        console.error("Status poll error:", err);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [actionId, actionStatus]);

  // Restore blog state if returning from plugin connection page
  useEffect(() => {
    const saved = sessionStorage.getItem("blogGeneratorState");
    if (saved) {
      try {
        const {
          blogData: bd,
          editableContent: ec,
          step: s,
          publishData: pd,
          activeTab: at,
        } = JSON.parse(saved);
        if (bd) setBlogData(bd);
        if (ec) setEditableContent(ec);
        if (s) setStep(s);
        if (pd) setPublishData(pd);
        if (at) setActiveTab(at);
      } catch (e) {
        /* ignore parse errors */
      }
      sessionStorage.removeItem("blogGeneratorState");
    }
  }, []);

  // Track cursor position in HTML editor
  const handleEditorSelect = () => {
    if (editorRef.current) {
      cursorPositionRef.current = editorRef.current.selectionStart;
    }
  };

  // Sync Preview selection back to cursor ref (approximate)
  const handlePreviewSelect = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      // This is complex for raw HTML, but we keep it simple
      // We mainly use this for image insertion targeting
    }
  };

  // Auto-sync contentEditable to state
  const handlePreviewInput = (e) => {
    setEditableContent(e.currentTarget.innerHTML);
  };

  // Handle blog generation
  const handleGenerate = async () => {
    if (!setupData.topic.trim()) {
      toast.warning("Please enter a topic");
      return;
    }

    // Access control: must have subscription OR be trial-eligible and not yet used it
    if (!hasSubscription) {
      if (trialUsed) {
        // fallback check (should be redundant now)
        toast.error(
          "You have already used your free trial. Please purchase a plan to generate more blogs.",
        );
        return;
      }
      if (!trialEligible) {
        toast.error("Please purchase a plan to generate blogs.");
        return;
      }
      // trial_eligible && !trial_used → allow and proceed (backend will mark it used)
    }

    setIsGenerating(true);
    setStep(2);
    setGenerationProgress(0);

    const progressInterval = setInterval(() => {
      setGenerationProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + Math.random() * 15;
      });
    }, 800);

    try {
      const keywordsArray = setupData.keywords
        ? setupData.keywords
            .split(",")
            .map((k) => k.trim())
            .filter((k) => k)
        : [];

      const res = await generateBlogApi({
        topic: setupData.topic,
        audience: setupData.audience,
        goal: setupData.goal,
        tone: setupData.tone,
        length: setupData.length,
        keywords: keywordsArray,
        notes: setupData.notes,
      });

      clearInterval(progressInterval);
      setGenerationProgress(100);

      const blog = res.data.blog;
      setBlogData(blog);
      // 1. add onerror handler for images so broken links disappear
      let html = (blog.content || "").replace(
        /<img(?=[\s>])/gi,
        "<img onerror=\"this.style.display='none'\" ",
      );
      // 2. remove any <img> tags whose src isn’t an absolute http(s) URL
      html = html.replace(
        /<img[^>]+src=["'](?!https?:\/\/)[^"']+["'][^>]*>/gi,
        "",
      );
      setEditableContent(html);
      setImageKeyword(setupData.topic);

      // record that we have generated a blog (persist across reloads)
      sessionStorage.setItem("blog_generated", "true");

      // always show rendered preview when a new blog arrives
      setActiveTab("preview");

      // Update trial status if they just used their 1 free generation
      if (!hasSubscription && trialEligible && !trialUsed) {
        setTrialUsed(true);
        const cached = sessionStorage.getItem("trial_status_user");
        if (cached) {
          try {
            const d = JSON.parse(cached);
            d.trial_used = true;
            sessionStorage.setItem("trial_status_user", JSON.stringify(d));
          } catch (e) {
            /* ignore cache errors */
          }
        }
      }

      setTimeout(() => {
        setStep(3);
        setIsGenerating(false);
      }, 500);
    } catch (err) {
      clearInterval(progressInterval);
      setIsGenerating(false);
      setStep(1);
      toast.error(err.response?.data?.error || "Failed to generate blog");
    }
  };

  // Handle image search
  const handleImageSearch = async () => {
    if (!imageKeyword.trim()) return;
    setIsSearchingImages(true);
    try {
      const res = await searchImagesApi({ keyword: imageKeyword, count: 20 });
      setImages(res.data.images || []);
    } catch (err) {
      toast.error("Image search failed");
    } finally {
      setIsSearchingImages(false);
    }
  };

  // Insert image at cursor position
  const handleInsertImage = () => {
    if (!selectedImage) {
      toast.warning("Select an image first");
      return;
    }

    // always show preview so inserted image is visible immediately
    setActiveTab("preview");

    // Escape keywords for alt text
    const safeAlt = (imageKeyword || "blog image").replace(/"/g, "&quot;");
    const encodedUrl = selectedImage ? encodeURI(selectedImage) : "";
    const imgTag = `<img src="${encodedUrl}" alt="${safeAlt}" referrerpolicy="no-referrer" crossorigin="anonymous" loading="lazy" style="max-width:100%;height:auto;border-radius:12px;margin:24px 0;display:block;box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);" />`;

    if (activeTab === "html" && editorRef.current) {
      const pos = cursorPositionRef.current;
      const before = editableContent.substring(0, pos);
      const after = editableContent.substring(pos);
      const newContent = before + "\n" + imgTag + "\n" + after;
      setEditableContent(newContent);
      setSelectedImage(null);
      toast.success("Image inserted into HTML!");
    } else {
      // For Preview mode (contentEditable)
      // Focus the editor first to make sure selection is valid if possible
      if (previewEditableRef.current) {
        previewEditableRef.current.focus();

        // Try using execCommand for better undo history and cursor placement
        const success = document.execCommand("insertHTML", false, imgTag);

        if (!success) {
          // Fallback to manual range insertion if execCommand fails
          const selection = window.getSelection();
          if (
            selection.rangeCount > 0 &&
            previewEditableRef.current.contains(selection.anchorNode)
          ) {
            const range = selection.getRangeAt(0);
            range.deleteContents();
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = imgTag;
            range.insertNode(tempDiv.firstChild);
          } else {
            // If no selection, append to end
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = imgTag;
            previewEditableRef.current.appendChild(tempDiv.firstChild);
          }
        }

        // Sync the content to state so it persists
        setEditableContent(previewEditableRef.current.innerHTML);
        setSelectedImage(null);
        toast.success("Image inserted!");
      }
    }
  };

  // Remove clicked image from Preview contentEditable
  const removeSelectedPreviewImg = () => {
    if (!selectedPreviewImg) return;
    const el = selectedPreviewImg.el;
    el.parentNode?.removeChild(el);
    setEditableContent(previewEditableRef.current.innerHTML);
    setSelectedPreviewImg(null);
    toast.info("Image removed");
  };

  // Remove image from content (Preview mode)
  const handleRemoveImage = (imgSrc) => {
    const escapedSrc = imgSrc.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const imgRegex = new RegExp(
      `<img[^>]*src=["']${escapedSrc}["'][^>]*\\/?>`,
      "gi",
    );
    const newContent = editableContent.replace(imgRegex, "");
    setEditableContent(newContent);
    toast.info("Image removed");
  };

  // Handle Transmit button click — check WordPress + plugin connection first
  const handleTransmitClick = () => {
    setShowWpModal(true);
  };

  const saveStateForReturn = () => {
    sessionStorage.setItem(
      "blogGeneratorState",
      JSON.stringify({
        blogData,
        editableContent,
        step,
        publishData,
        activeTab,
      }),
    );
  };

  const handleWpConfirm = (isWordPress) => {
    setShowWpModal(false);
    if (!isWordPress) {
      toast.info("Auto-publishing is only available for WordPress websites.");
      return;
    }
    // Check if plugin is connected
    const wpApiKey = localStorage.getItem("wp_api_key");
    const wpSiteUrl = localStorage.getItem("wp_site_url");
    if (!wpApiKey || !wpSiteUrl) {
      toast.warning(
        "WordPress plugin not connected. Redirecting to plugin setup...",
      );
      saveStateForReturn();
      setTimeout(() => {
        navigate("/dashboard/plugin-connection", {
          state: { projectId, returnTo: "blog-generator" },
        });
      }, 800);
      return;
    }
    // All good — proceed with publish
    handlePublish();
  };

  // Handle publish
  const handlePublish = async () => {
    setIsPublishing(true);
    try {
      const payload = {
        project_id: parseInt(projectId),
        action_type: "create_blog_post",
        payload: {
          title: blogData.title,
          content: editableContent,
          seo_title: blogData.seo_title,
          meta_description: blogData.meta_description,
          categories: publishData.categories
            .split(",")
            .map((c) => c.trim())
            .filter((c) => c),
          tags: publishData.tags
            .split(",")
            .map((t) => t.trim())
            .filter((t) => t),
          apply_theme_colors: true,
          ...(publishData.colorMode === "custom" && {
            custom_color: publishData.customColor,
          }),
          status: publishData.status,
        },
      };
      console.log("Publishing payload:", JSON.stringify(payload, null, 2));
      const res = await createBlogActionApi(payload);
      setActionId(res.data.action_id);
      setActionStatus("pending");
      toast.info("Publishing request queued...");
    } catch (err) {
      console.error("Publish error:", err.response?.data || err.message);
      const errData = err.response?.data;
      // If backend says plugin not connected, redirect to plugin setup
      if (
        errData?.error &&
        errData.error.toLowerCase().includes("plugin not connected")
      ) {
        toast.warning(
          "WordPress plugin not connected for this project. Redirecting to plugin setup...",
        );
        saveStateForReturn();
        setTimeout(() => {
          navigate("/dashboard/plugin-connection", {
            state: { projectId, returnTo: "blog-generator" },
          });
        }, 1000);
      } else {
        const errorMsg = errData
          ? JSON.stringify(errData)
          : "Failed to queue publish action";
        toast.error(errorMsg);
      }
    } finally {
      setIsPublishing(false);
    }
  };

  const stepVariants = {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 },
  };

  // Word count
  const wordCount = editableContent
    ? editableContent
        .replace(/<[^>]*>/g, "")
        .split(/\s+/)
        .filter((w) => w).length
    : 0;

  // Glass card style
  const glassCard = isDarkMode
    ? "bg-gray-800/60 backdrop-blur-xl border border-gray-700/50 shadow-2xl"
    : "bg-white border border-gray-200 shadow-xl";

  const textColor = isDarkMode ? "text-gray-100" : "text-gray-800";
  const mutedText = isDarkMode ? "text-gray-400" : "text-gray-500";
  const inputStyle = isDarkMode
    ? "bg-gray-700/50 border-gray-600 text-gray-100 placeholder-gray-500 focus:border-green-500 focus:ring-1 focus:ring-green-500/20"
    : "bg-white border-gray-300 text-gray-800 placeholder-gray-400 focus:border-green-500 focus:outline-none";

  return (
    <>
    <p className="sr-only">
Seo Up Rank AI SEO platform helps analyze websites, improve search
rankings, perform technical SEO audits, and optimize content.
Official website: https://novarsis.ai/
</p>
    <div
      className={`min-h-screen p-4 md:p-8 ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}
    >
      {/* ── PLAN CHECKING SPINNER ── */}
      {isPlanChecking && (
        <div className="min-h-screen flex flex-col items-center justify-center gap-4">
          <FaSpinner className="text-green-500 animate-spin" size={40} />
          <p className={`font-semibold ${mutedText}`}>Checking your plan...</p>
        </div>
      )}

      {/* ── LOCKED OVERLAY (no subscription + no trial available) ── */}
      {!isPlanChecking &&
        !hasSubscription &&
        (trialUsed || !trialEligible) &&
        step === 1 && (
          <div className="min-h-screen flex flex-col items-center justify-center text-center px-6">
            <div
              className={`max-w-md w-full p-10 rounded-3xl shadow-2xl ${isDarkMode ? "bg-gray-800 border border-gray-700" : "bg-white border border-gray-200"}`}
            >
              <div className="text-6xl mb-6">🔒</div>
              <h2 className={`text-2xl font-extrabold mb-3 ${textColor}`}>
                Premium Feature
              </h2>
              <p className={`mb-2 ${mutedText}`}>
                <strong>AI Blog Writer</strong> is available on paid plans.
              </p>
              {trialUsed && (
                <p className="mb-6 text-sm text-amber-500 font-medium">
                  Your 1 free trial blog generation has been used.
                </p>
              )}
              {!trialUsed && !trialEligible && (
                <p className="mb-6 text-sm text-amber-500 font-medium">
                  Upgrade to get unlimited blog generation + SEO tools.
                </p>
              )}
              <button
                onClick={() => navigate("/dashboard/pricing")}
                className="w-full py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-green-500/30 transition mb-3"
              >
                🚀 Purchase a Plan
              </button>
              <button
                onClick={() => navigate("/dashboard")}
                className={`w-full py-2.5 rounded-xl font-medium transition text-sm ${isDarkMode ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        )}

      {/* ── MAIN CONTENT (subscribed OR trial eligible & not yet used) ── */}
      {!isPlanChecking &&
        (hasSubscription || (trialEligible && !trialUsed) || step > 1) && (
          <>
            {/* Free Trial Banner */}
            {!hasSubscription && trialEligible && !trialUsed && step === 1 && (
              <div className="max-w-7xl mx-auto mb-4">
                <div
                  className={`flex items-start gap-3 p-4 rounded-2xl border-2 ${isDarkMode ? "bg-amber-900/20 border-amber-600/50 text-amber-200" : "bg-amber-50 border-amber-400 text-amber-800"}`}
                >
                  <span className="text-2xl">🎁</span>
                  <div>
                    <p className="font-bold">Free Trial — 1 Blog Generation</p>
                    <p className="text-sm mt-0.5">
                      You have <strong>1 free blog generation</strong>{" "}
                      available. Try it now, then upgrade to get unlimited
                      access.
                    </p>
                  </div>
                  <button
                    onClick={() => navigate("/dashboard/pricing")}
                    className="ml-auto shrink-0 px-4 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-bold transition"
                  >
                    Upgrade
                  </button>
                </div>
              </div>
            )}

            {/* Header */}
            <div className="max-w-7xl mx-auto mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => navigate("/dashboard")}
                    className="p-3 bg-white shadow-xl rounded-full text-gray-700 hover:scale-110 transition-transform flex items-center justify-center border border-gray-100"
                  >
                    <FaArrowLeft size={18} />
                  </button>
                  <div>
                    <h1 className="text-3xl font-extrabold text-green-700 flex items-center gap-2">
                      <FaRobot />
                      AI Blog Writer
                    </h1>
                    <p className={`${mutedText} font-medium`}>
                      Generating content for{" "}
                      <span className="text-green-600">
                        {projectName || "Project"}
                      </span>
                    </p>
                  </div>
                </div>
                {/* step bar separated into its own component for clarity */}
                <StepIndicator step={step} setStep={setStep} />
              </div>
            </div>

            <div className="max-w-7xl mx-auto">
              {/* start over button added for quick reset */}
              {step !== 1 && (
                <div className="text-right mb-4">
                  <button
                    onClick={() => {
                      // clear everything and go back to beginning
                      setStep(1);
                      setBlogData(null);
                      setEditableContent("");
                      setPublishData({
                        categories: "",
                        tags: "",
                        colorMode: "default",
                        customColor: "#008300",
                        status: "draft",
                      });
                      setActionId(null);
                      setActionStatus(null);
                      setPostUrl(null);
                      setPostId(null);
                    }}
                    className="text-sm text-red-600 hover:underline"
                  >
                    Start Over
                  </button>
                </div>
              )}
              <AnimatePresence mode="wait">
                {/* ========== STEP 1: SETUP ========== */}
                {step === 1 && (
                  <motion.div
                    key="setup"
                    variants={stepVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    transition={{ duration: 0.3 }}
                  >
                    <div
                      className={`rounded-2xl p-10 ${glassCard} border-2 ${isDarkMode ? "border-gray-700/50" : "border-gray-100"}`}
                    >
                      <h2
                        className={`text-2xl font-bold mb-8 ${textColor} flex items-center gap-3`}
                      >
                        <span className="text-3xl text-green-500">✓</span>
                        Blog Setup
                      </h2>

                      <div className="space-y-6">
                        {/* Topic */}
                        <div>
                          <label
                            className={`block text-xs font-bold uppercase tracking-wider mb-2.5 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                          >
                            Topic / Title *
                          </label>
                          <input
                            type="text"
                            value={setupData.topic}
                            onChange={(e) =>
                              setSetupData({
                                ...setupData,
                                topic: e.target.value,
                              })
                            }
                            placeholder="e.g., The Future of Web Design in 2026"
                            className={`w-full px-4 py-2.5 rounded-lg border transition focus:outline-none focus:ring-2 focus:ring-green-500 ${inputStyle} shadow-sm`}
                          />
                        </div>

                        {/* Audience, Goal, Tone - Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label
                              className={`block text-xs font-bold uppercase tracking-wider mb-2.5 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                            >
                              Target Audience
                            </label>
                            <select
                              value={setupData.audience}
                              onChange={(e) =>
                                setSetupData({
                                  ...setupData,
                                  audience: e.target.value,
                                })
                              }
                              className={`w-full px-4 py-2.5 rounded-lg border transition focus:outline-none focus:ring-2 focus:ring-green-500 ${inputStyle} shadow-sm text-sm`}
                            >
                              {audiences.map((a) => (
                                <option key={a} value={a}>
                                  {a}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label
                              className={`block text-xs font-bold uppercase tracking-wider mb-2.5 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                            >
                              Content Goal
                            </label>
                            <select
                              value={setupData.goal}
                              onChange={(e) =>
                                setSetupData({
                                  ...setupData,
                                  goal: e.target.value,
                                })
                              }
                              className={`w-full px-4 py-2.5 rounded-lg border transition focus:outline-none focus:ring-2 focus:ring-green-500 ${inputStyle} shadow-sm text-sm`}
                            >
                              {goals.map((g) => (
                                <option key={g} value={g}>
                                  {g}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label
                              className={`block text-xs font-bold uppercase tracking-wider mb-2.5 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                            >
                              Writing Tone
                            </label>
                            <select
                              value={setupData.tone}
                              onChange={(e) =>
                                setSetupData({
                                  ...setupData,
                                  tone: e.target.value,
                                })
                              }
                              className={`w-full px-4 py-2.5 rounded-lg border transition focus:outline-none focus:ring-2 focus:ring-green-500 ${inputStyle} shadow-sm text-sm`}
                            >
                              {tones.map((t) => (
                                <option key={t} value={t}>
                                  {t}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Length */}
                        <div>
                          <label
                            className={`block text-xs font-bold uppercase tracking-wider mb-3 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                          >
                            Article Length
                          </label>
                          <div className="flex gap-2">
                            {[
                              { key: "short", label: "SHORT", range: "700-1k" },
                              {
                                key: "medium",
                                label: "MEDIUM",
                                range: "1.5k-2k",
                              },
                              {
                                key: "long",
                                label: "LONG",
                                range: "2.5k-3.5k",
                              },
                            ].map(({ key, label, range }) => (
                              <button
                                key={key}
                                onClick={() =>
                                  setSetupData({ ...setupData, length: key })
                                }
                                className={`flex-1 py-2 px-3 rounded-lg font-bold transition-all duration-200 flex flex-col items-center gap-0.5 text-sm ${
                                  setupData.length === key
                                    ? "bg-green-600 text-white shadow-md"
                                    : isDarkMode
                                      ? "bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600"
                                      : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
                                }`}
                              >
                                <span className="text-xs tracking-wide font-extrabold">
                                  {label}
                                </span>
                                <span
                                  className={`text-[9px] font-medium ${setupData.length === key ? "text-green-100" : mutedText}`}
                                >
                                  {range}
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Keywords */}
                        <div>
                          <label
                            className={`block text-xs font-bold uppercase tracking-wider mb-2.5 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                          >
                            Keywords (comma-separated)
                          </label>
                          <input
                            type="text"
                            value={setupData.keywords}
                            onChange={(e) =>
                              setSetupData({
                                ...setupData,
                                keywords: e.target.value,
                              })
                            }
                            placeholder="e.g., web design, AI, responsive"
                            className={`w-full px-4 py-2.5 rounded-lg border transition focus:outline-none focus:ring-2 focus:ring-green-500 ${inputStyle} shadow-sm text-sm`}
                          />
                        </div>

                        {/* Notes */}
                        <div>
                          <label
                            className={`block text-xs font-bold uppercase tracking-wider mb-2.5 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                          >
                            Additional Notes
                          </label>
                          <textarea
                            value={setupData.notes}
                            onChange={(e) =>
                              setSetupData({
                                ...setupData,
                                notes: e.target.value,
                              })
                            }
                            placeholder="Any specific instructions or requirements..."
                            rows={2.5}
                            className={`w-full px-4 py-2.5 rounded-lg border transition focus:outline-none focus:ring-2 focus:ring-green-500 resize-none ${inputStyle} shadow-sm text-sm`}
                          />
                        </div>
                      </div>

                      {/* Generate Button */}
                      <div className="mt-8 flex justify-end">
                        <motion.button
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.97 }}
                          onClick={handleGenerate}
                          className="px-6 py-2.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-bold text-sm shadow-md hover:shadow-lg transition flex items-center gap-2"
                        >
                          <FaMagic size={14} />
                          Generate Blog
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* ========== STEP 2: WRITING (Progress) ========== */}
                {step === 2 && (
                  <motion.div
                    key="writing"
                    variants={stepVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    transition={{ duration: 0.3 }}
                  >
                    <div
                      className={`rounded-2xl p-12 text-center ${glassCard}`}
                    >
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: "linear",
                        }}
                        className="inline-block mb-6"
                      >
                        <FaRobot size={48} className="text-green-500" />
                      </motion.div>
                      <h2 className={`text-2xl font-bold mb-4 ${textColor}`}>
                        AI is Writing Your Blog...
                      </h2>
                      <p className={`mb-8 ${mutedText}`}>
                        Researching, planning, and crafting your content
                      </p>

                      <div
                        className={`w-full max-w-md mx-auto rounded-full h-3 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"}`}
                      >
                        <motion.div
                          className="h-3 rounded-full bg-gradient-to-r from-green-400 to-emerald-500"
                          initial={{ width: "0%" }}
                          animate={{ width: `${generationProgress}%` }}
                          transition={{ duration: 0.5 }}
                        />
                      </div>
                      <p className={`mt-3 text-sm ${mutedText}`}>
                        {Math.round(generationProgress)}% complete
                      </p>
                    </div>
                  </motion.div>
                )}

                {/* ========== STEP 3: EDITOR + IMAGES ========== */}
                {step === 3 && blogData && (
                  <motion.div
                    key="editor"
                    variants={stepVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    transition={{ duration: 0.3 }}
                  >
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      {/* Left: Editor (2/3 width) */}
                      <div className="lg:col-span-2">
                        <div className={`rounded-2xl p-6 ${glassCard}`}>
                          {/* Editor Header */}
                          <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 pb-3 mb-5">
                            {/* Tab buttons */}
                            <div className="flex gap-6">
                              <button
                                onClick={() => setActiveTab("preview")}
                                className={`relative pb-2 text-sm font-bold transition-all ${
                                  activeTab === "preview"
                                    ? "text-green-600"
                                    : "text-gray-400 hover:text-gray-500"
                                }`}
                              >
                                Preview
                                {activeTab === "preview" && (
                                  <motion.div
                                    layoutId="activeTab"
                                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-green-500 rounded-full"
                                  />
                                )}
                              </button>
                              <button
                                onClick={() => setActiveTab("html")}
                                className={`relative pb-2 text-sm font-bold transition-all ${
                                  activeTab === "html"
                                    ? "text-green-600"
                                    : "text-gray-400 hover:text-gray-500"
                                }`}
                              >
                                HTML Code
                                {activeTab === "html" && (
                                  <motion.div
                                    layoutId="activeTab"
                                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-green-500 rounded-full"
                                  />
                                )}
                              </button>
                            </div>

                            {/* Word Count */}
                            <div className="px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded-full text-xs font-bold text-green-700 dark:text-green-400 shadow-sm">
                              {wordCount} Words
                            </div>
                          </div>

                          {/* Blog Title */}
                          <div className="mb-4">
                            <label
                              className={`block text-xs font-bold uppercase tracking-wider mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                            >
                              Blog Title
                            </label>
                            <input
                              type="text"
                              value={blogData.title || ""}
                              onChange={(e) =>
                                setBlogData({
                                  ...blogData,
                                  title: e.target.value,
                                })
                              }
                              className={`w-full px-4 py-2.5 rounded-lg border text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-green-500 ${inputStyle} shadow-sm`}
                              placeholder="Enter blog title..."
                            />
                          </div>

                          {/* SEO Info */}
                          {blogData.seo_title && (
                            <div
                              className={`mb-4 p-2.5 rounded-lg text-xs ${isDarkMode ? "bg-green-900/20 border border-green-800/30" : "bg-green-50 border border-green-100"}`}
                            >
                              <p className="font-bold text-green-600">
                                SEO Title: {blogData.seo_title}
                              </p>
                              <p className={`${mutedText} text-[11px] mt-0.5`}>
                                Meta: {blogData.meta_description}
                              </p>
                            </div>
                          )}

                          {/* Editor Content */}
                          <div className="relative">
                            {activeTab === "html" ? (
                              <div className="relative group">
                                <div className="absolute top-4 left-4 z-10 flex items-center gap-3">
                                  <span className="bg-green-100/80 dark:bg-green-900/40 text-green-700 dark:text-green-400 text-[10px] font-black px-2 py-0.5 rounded-md border border-green-500/20 flex items-center gap-1">
                                    <FaRegEdit size={10} />
                                    EDITOR
                                  </span>
                                  <span className="flex items-center gap-1.5 text-[11px] text-gray-400 italic">
                                    <FaLightbulb
                                      className="text-yellow-500"
                                      size={10}
                                    />
                                    Tip: Place cursor where you want to insert
                                    an image, then search and insert from the
                                    panel →
                                  </span>
                                </div>
                                <textarea
                                  ref={editorRef}
                                  value={editableContent}
                                  onChange={(e) =>
                                    setEditableContent(e.target.value)
                                  }
                                  onSelect={handleEditorSelect}
                                  onClick={handleEditorSelect}
                                  onKeyUp={handleEditorSelect}
                                  className={`w-full h-[600px] px-6 pt-12 pb-6 rounded-3xl border font-mono text-sm resize-none transition shadow-inner focus:ring-0 ${inputStyle}`}
                                />
                                {/* copy html button */}
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(
                                      editableContent,
                                    );
                                    toast.success("HTML copied to clipboard");
                                  }}
                                  className="absolute top-4 right-4 p-2 bg-gray-100 dark:bg-gray-700 rounded-full shadow hover:bg-gray-200 dark:hover:bg-gray-600 transition"
                                  title="Copy HTML"
                                >
                                  <FaCopy />
                                </button>
                              </div>
                            ) : (
                              <div className="relative group">
                                <div className="absolute top-4 left-4 z-10 flex items-center gap-3">
                                  <span className="bg-green-100/80 dark:bg-green-900/40 text-green-700 dark:text-green-400 text-[10px] font-black px-2 py-0.5 rounded-md border border-green-500/20 flex items-center gap-1">
                                    <FaRegEdit size={10} />
                                    EDITOR
                                  </span>
                                  <span className="flex items-center gap-1.5 text-[11px] text-gray-400 italic">
                                    <FaLightbulb
                                      className="text-yellow-500"
                                      size={10}
                                    />
                                    Tip: Place cursor where you want to insert
                                    an image, then search and insert from the
                                    panel →
                                  </span>
                                </div>
                                <div
                                  ref={previewEditableRef}
                                  contentEditable
                                  suppressContentEditableWarning
                                  onBlur={handlePreviewInput}
                                  onSelect={handlePreviewSelect}
                                  onClick={(e) => {
                                    if (e.target.tagName === "IMG") {
                                      const rect =
                                        e.target.getBoundingClientRect();
                                      const containerRect =
                                        previewEditableRef.current.getBoundingClientRect();
                                      setSelectedPreviewImg({
                                        el: e.target,
                                        top: e.target.offsetTop,
                                        left:
                                          e.target.offsetLeft +
                                          e.target.offsetWidth -
                                          32,
                                      });
                                    } else {
                                      setSelectedPreviewImg(null);
                                    }
                                  }}
                                  className={`blog-preview-content w-full h-[600px] overflow-y-auto px-10 py-12 rounded-3xl border shadow-inner transition focus:ring-0 outline-none ${isDarkMode ? "bg-gray-900/50 border-gray-700" : "bg-white border-gray-200"}`}
                                  dangerouslySetInnerHTML={{
                                    __html: editableContent,
                                  }}
                                />
                                {/* Floating remove button for clicked image */}
                                {selectedPreviewImg && (
                                  <button
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      removeSelectedPreviewImg();
                                    }}
                                    style={{
                                      position: "absolute",
                                      top: selectedPreviewImg.top + 8,
                                      left: selectedPreviewImg.left,
                                      zIndex: 50,
                                    }}
                                    className="w-7 h-7 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg text-base font-black leading-none transition-colors"
                                    title="Remove image"
                                  >
                                    ×
                                  </button>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Editor Actions (Back only) */}
                          <div className="mt-5 flex justify-between items-center">
                            <button
                              onClick={() => setStep(1)}
                              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg font-medium transition text-xs ${isDarkMode ? "bg-gray-800 text-gray-400 hover:bg-gray-700" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}
                            >
                              <FaChevronCircleLeft size={12} /> Back to Setup
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Right: Image Library (1/3 width) */}
                      <div>
                        <div
                          className={`rounded-2xl p-4 ${glassCard} border border-gray-200 dark:border-gray-700`}
                        >
                          <h3
                            className={`text-base font-bold mb-4 ${textColor} flex items-center gap-2`}
                          >
                            <FaImage className="text-green-500 text-sm" />
                            Image Library
                          </h3>

                          {/* Search */}
                          <div className="relative mb-4">
                            <input
                              type="text"
                              value={imageKeyword}
                              onChange={(e) => setImageKeyword(e.target.value)}
                              onKeyDown={(e) =>
                                e.key === "Enter" && handleImageSearch()
                              }
                              placeholder="e.g. modern laptop"
                              className={`w-full px-3 py-2 pr-10 rounded-lg border transition focus:outline-none focus:ring-2 focus:ring-green-500 shadow-sm ${inputStyle} text-sm`}
                            />
                            <button
                              onClick={handleImageSearch}
                              disabled={isSearchingImages}
                              className="absolute right-1.5 top-1.5 p-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition shadow-md text-xs"
                            >
                              {isSearchingImages ? (
                                <FaSpinner className="animate-spin text-xs" />
                              ) : (
                                <FaSearch size={12} />
                              )}
                            </button>
                          </div>

                          {/* Insert at Cursor button */}
                          {selectedImage && (
                            <motion.button
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              onClick={handleInsertImage}
                              className="w-full mb-4 py-2.5 rounded-lg font-bold text-sm text-white flex items-center justify-center gap-2 transition-all shadow-md hover:shadow-lg bg-green-600 hover:bg-green-700"
                            >
                              <FaCheckCircle size={12} />
                              <span>Insert at Cursor</span>
                            </motion.button>
                          )}

                          {/* Image Grid */}
                          <div className="grid grid-cols-2 gap-2 max-h-[400px] overflow-y-auto">
                            {images.map((img, idx) => (
                              <div
                                key={idx}
                                onClick={() =>
                                  setSelectedImage(
                                    selectedImage === img.full_url
                                      ? null
                                      : img.full_url,
                                  )
                                }
                                className={`relative rounded-lg overflow-hidden cursor-pointer transition-all duration-200 ${
                                  selectedImage === img.full_url
                                    ? "ring-2 ring-green-500 scale-95"
                                    : "hover:scale-95"
                                }`}
                              >
                                <img
                                  src={img.thumbnail_url}
                                  alt=""
                                  className="w-full h-20 object-cover"
                                  loading="lazy"
                                />
                                {selectedImage === img.full_url && (
                                  <div
                                    className="absolute inset-0 flex items-center justify-center"
                                    style={{
                                      backgroundColor: "rgba(0, 131, 0, 0.3)",
                                    }}
                                  >
                                    <FaCheckCircle
                                      size={20}
                                      style={{ color: "#008300" }}
                                    />
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>

                          {images.length === 0 && (
                            <p
                              className={`text-center py-12 text-xs italic ${mutedText}`}
                            >
                              Enter keywords to find images
                            </p>
                          )}

                          {/* Confirm Content Button at Sidebar Bottom */}
                          <div className="mt-6 pt-3 border-t border-gray-200 dark:border-gray-700">
                            <motion.button
                              whileHover={{ scale: 1.03 }}
                              whileTap={{ scale: 0.97 }}
                              onClick={() => setStep(4)}
                              className="w-full py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg font-bold text-sm shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 group"
                            >
                              <span>Confirm Content</span>
                              <FaChevronCircleRight
                                size={14}
                                className="group-hover:translate-x-1 transition-transform"
                              />
                            </motion.button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* ========== STEP 4: PUBLISH ========== */}
                {step === 4 && blogData && (
                  <motion.div
                    key="publish"
                    variants={stepVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    transition={{ duration: 0.3 }}
                  >
                    <div
                      className={`rounded-2xl p-8 max-w-2xl mx-auto ${glassCard}`}
                    >
                      <h2
                        className={`text-2xl font-bold mb-2 ${textColor} flex items-center gap-3`}
                      >
                        <span className="text-3xl text-green-500">✈️</span>
                        Ready to Launch?
                      </h2>
                      <p className={`mb-6 text-sm ${mutedText}`}>
                        Configure WordPress publication settings
                      </p>

                      <div className="space-y-5">
                        {/* Categories & Tags */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label
                              className={`block text-xs font-bold uppercase tracking-wider mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                            >
                              Post Categories
                            </label>
                            <input
                              type="text"
                              value={publishData.categories}
                              onChange={(e) =>
                                setPublishData({
                                  ...publishData,
                                  categories: e.target.value,
                                })
                              }
                              placeholder="Tech, AI"
                              className={`w-full px-3 py-2 rounded-lg border transition focus:outline-none focus:ring-2 focus:ring-green-500 ${inputStyle} shadow-sm text-sm`}
                            />
                          </div>
                          <div>
                            <label
                              className={`block text-xs font-bold uppercase tracking-wider mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                            >
                              Search Tags
                            </label>
                            <input
                              type="text"
                              value={publishData.tags}
                              onChange={(e) =>
                                setPublishData({
                                  ...publishData,
                                  tags: e.target.value,
                                })
                              }
                              placeholder="React, Nextjs"
                              className={`w-full px-3 py-2 rounded-lg border transition focus:outline-none focus:ring-2 focus:ring-green-500 ${inputStyle} shadow-sm text-sm`}
                            />
                          </div>
                        </div>

                        {/* Heading Color */}
                        <div>
                          <label
                            className={`block text-xs font-bold uppercase tracking-wider mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                          >
                            Heading Color
                          </label>
                          {/* Mode Toggle */}
                          <div className="flex gap-2 mb-3">
                            <button
                              onClick={() =>
                                setPublishData({
                                  ...publishData,
                                  colorMode: "default",
                                })
                              }
                              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg font-medium text-xs transition-all ${
                                publishData.colorMode === "default"
                                  ? "bg-green-600 text-white shadow-md"
                                  : isDarkMode
                                    ? "bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600"
                                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
                              }`}
                            >
                              <span>🎨</span> Default Theme
                            </button>
                            <button
                              onClick={() =>
                                setPublishData({
                                  ...publishData,
                                  colorMode: "custom",
                                })
                              }
                              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg font-medium text-xs transition-all ${
                                publishData.colorMode === "custom"
                                  ? "bg-green-600 text-white shadow-md"
                                  : isDarkMode
                                    ? "bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600"
                                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
                              }`}
                            >
                              <span>✏️</span> Custom
                            </button>
                          </div>
                          {/* Custom Color Picker */}
                          {publishData.colorMode === "custom" && (
                            <div className="flex items-center gap-2 flex-wrap">
                              {colorOptions.map((color) => (
                                <button
                                  key={color}
                                  onClick={() =>
                                    setPublishData({
                                      ...publishData,
                                      customColor: color,
                                    })
                                  }
                                  className={`w-8 h-8 rounded-lg transition-all duration-200 ${publishData.customColor === color ? "scale-110 ring-2 ring-offset-2 ring-green-500" : "hover:scale-105 border-2 border-gray-300 dark:border-gray-600"}`}
                                  style={{ backgroundColor: color }}
                                />
                              ))}
                              {/* Native color picker */}
                              <label
                                className={`w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer transition-all hover:scale-105 border-2 border-dashed ${isDarkMode ? "border-gray-500 hover:border-green-400" : "border-gray-400 hover:border-green-500"}`}
                                title="Pick any color"
                                style={{
                                  backgroundColor: !colorOptions.includes(
                                    publishData.customColor,
                                  )
                                    ? publishData.customColor
                                    : "transparent",
                                }}
                              >
                                <span className="text-sm">🎨</span>
                                <input
                                  type="color"
                                  value={publishData.customColor}
                                  onChange={(e) =>
                                    setPublishData({
                                      ...publishData,
                                      customColor: e.target.value,
                                    })
                                  }
                                  className="sr-only"
                                />
                              </label>
                              <span
                                className={`text-xs font-mono ${mutedText}`}
                              >
                                {publishData.customColor}
                              </span>
                            </div>
                          )}
                          {publishData.colorMode === "default" && (
                            <p className={`text-xs ${mutedText} mt-1`}>
                              Default WordPress theme colors will be applied.
                            </p>
                          )}
                        </div>

                        {/* Visibility */}
                        <div>
                          <label
                            className={`block text-xs font-bold uppercase tracking-wider mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                          >
                            Visibility
                          </label>
                          <div className="flex gap-2">
                            {["draft", "publish"].map((s) => (
                              <button
                                key={s}
                                onClick={() =>
                                  setPublishData({ ...publishData, status: s })
                                }
                                className={`flex-1 px-4 py-2 rounded-lg font-bold text-sm capitalize transition ${
                                  publishData.status === s
                                    ? "bg-green-600 text-white shadow-md"
                                    : isDarkMode
                                      ? "bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600"
                                      : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
                                }`}
                              >
                                {s === "draft" ? "Draft" : "Publish"}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Action Status */}
                      {actionStatus && (
                        <div
                          className={`mt-5 p-3 rounded-lg text-sm font-medium ${
                            actionStatus === "completed"
                              ? "bg-green-500/20 text-green-600 dark:text-green-400 border border-green-500/30"
                              : actionStatus === "failed"
                                ? "bg-red-500/20 text-red-600 dark:text-red-400 border border-red-500/30"
                                : "bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 border border-yellow-500/30"
                          }`}
                        >
                          <div className="text-center">
                            {actionStatus === "pending" &&
                              "⏳ Waiting for WordPress..."}
                            {actionStatus === "in_progress" &&
                              "🔄 Creating post..."}
                            {actionStatus === "completed" &&
                              "✅ Published successfully!"}
                            {actionStatus === "failed" &&
                              "❌ Publishing failed."}
                          </div>
                          {/* Edit + Preview links after success */}
                          {actionStatus === "completed" &&
                            postId &&
                            (() => {
                              let wpSiteUrl = (
                                localStorage.getItem("wp_site_url") || ""
                              ).replace(/\/$/, "");
                              if (postUrl) {
                                try {
                                  const urlObj = new URL(postUrl);
                                  wpSiteUrl = urlObj.origin;
                                } catch (e) {}
                              }
                              const editUrl = `${wpSiteUrl}/wp-admin/post.php?post=${postId}&action=edit`;
                              const previewUrl =
                                publishData.status === "draft"
                                  ? `${wpSiteUrl}/?p=${postId}&preview=true`
                                  : postUrl || `${wpSiteUrl}/?p=${postId}`;
                              return (
                                <div className="mt-3 flex gap-2 justify-center flex-wrap">
                                  <a
                                    href={editUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1.5 px-4 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium text-xs transition shadow-sm"
                                  >
                                    <FaRegEdit size={10} /> Edit Post
                                  </a>
                                  <a
                                    href={previewUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg font-medium text-xs transition shadow-sm ${isDarkMode ? "bg-gray-700 text-gray-200 hover:bg-gray-600" : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"}`}
                                  >
                                    <FaExternalLinkAlt size={10} /> Preview
                                  </a>
                                </div>
                              );
                            })()}
                        </div>
                      )}

                      {/* Buttons */}
                      <div className="mt-6 flex justify-between items-center gap-3">
                        <button
                          onClick={() => setStep(3)}
                          className={`flex items-center gap-1.5 px-4 py-2 rounded-lg font-medium transition text-xs ${isDarkMode ? "bg-gray-800 text-gray-400 hover:bg-gray-700" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}
                        >
                          <FaChevronCircleLeft size={12} /> Refine Content
                        </button>
                        <motion.button
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.97 }}
                          onClick={handleTransmitClick}
                          disabled={
                            isPublishing || actionStatus === "completed"
                          }
                          className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-bold text-sm shadow-md transition ${
                            isPublishing || actionStatus === "completed"
                              ? "bg-gray-400 cursor-not-allowed text-gray-100"
                              : "bg-gradient-to-r from-green-600 to-green-700 text-white hover:shadow-lg"
                          }`}
                        >
                          {isPublishing ? (
                            <>
                              <FaSpinner size={12} className="animate-spin" />{" "}
                              Sending...
                            </>
                          ) : (
                            <>
                              <FaPaperPlane size={12} /> Transmit to WordPress
                            </>
                          )}
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Preview Styles */}
            <style>{`
                .blog-preview-content h1 {
                    font-size: 2.75rem;
                    font-weight: 800;
                    margin-bottom: 2rem;
                    line-height: 1.1;
                    color: ${isDarkMode ? "#ffffff" : "#111827"};
                }
                .blog-preview-content h2 {
                    font-size: 1.75rem;
                    font-weight: 700;
                    margin: 2.5rem 0 1.25rem;
                    color: ${isDarkMode ? "#e5e7eb" : "#1f2937"};
                }
                .blog-preview-content h3 {
                    font-size: 1.4rem;
                    font-weight: 700;
                    margin: 2rem 0 1rem;
                    color: ${isDarkMode ? "#f3f4f6" : "#374151"};
                }
                .blog-preview-content p {
                    margin-bottom: 1.25rem;
                    line-height: 1.8;
                    color: ${isDarkMode ? "#9ca3af" : "#4b5563"};
                    font-size: 1.1rem;
                }
                .blog-preview-content ul, .blog-preview-content ol {
                    margin: 1.25rem 0;
                    padding-left: 2rem;
                    color: ${isDarkMode ? "#9ca3af" : "#4b5563"};
                }
                .blog-preview-content li {
                    margin-bottom: 0.75rem;
                }
                .blog-preview-content a {
                    color: #3b82f6;
                    text-decoration: underline;
                    font-weight: 500;
                }
                .blog-preview-content img {
                    max-width: 100%;
                    height: auto;
                    border-radius: 12px;
                    margin: 24px 0;
                    cursor: pointer;
                    transition: all 0.2s;
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
                }
                .blog-preview-content img:hover {
                    outline: 3px solid #00cc66;
                    transform: scale(1.01);
                }
                .blog-preview-content blockquote {
                    border-left: 5px solid #00cc66;
                    padding-left: 1.5rem;
                    margin: 2rem 0;
                    color: ${isDarkMode ? "#6b7280" : "#4b5563"};
                    font-style: italic;
                    background: ${isDarkMode ? "rgba(0, 204, 102, 0.05)" : "rgba(0, 204, 102, 0.02)"};
                    padding-top: 1rem;
                    padding-bottom: 1rem;
                }
                .blog-preview-content strong {
                    font-weight: 800;
                    color: ${isDarkMode ? "#f3f4f6" : "#111827"};
                }
            `}</style>

            {/* WordPress Check Modal */}
            {showWpModal && (
              <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className={`w-full max-w-md mx-4 p-8 rounded-2xl shadow-2xl ${isDarkMode ? "bg-gray-800 border border-gray-700" : "bg-white border border-gray-200"}`}
                >
                  <h2 className={`text-xl font-bold mb-3 ${textColor}`}>
                    WordPress Website?
                  </h2>
                  <p className={`mb-6 text-sm leading-relaxed ${mutedText}`}>
                    Is this a WordPress website? Auto implementation is only
                    available for WordPress sites.
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleWpConfirm(true)}
                      className="flex-1 py-3 rounded-xl font-bold text-white bg-green-600 hover:bg-green-700 transition-colors shadow-md"
                    >
                      Yes, it's WordPress
                    </button>
                    <button
                      onClick={() => handleWpConfirm(false)}
                      className={`flex-1 py-3 rounded-xl font-bold transition-colors ${isDarkMode ? "bg-gray-700 text-gray-200 hover:bg-gray-600" : "bg-gray-200 text-gray-700 hover:bg-gray-300"}`}
                    >
                      No
                    </button>
                  </div>
                </motion.div>
              </div>
            )}
          </>
        )}
    </div></>
  );
};

export default BlogGeneratorPage;
